import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eraser, PenSquare } from "lucide-react";

/**
 * SignaturePad — HTML5 canvas signature capture.
 * Emits `onChange(dataUrl)` with a base64 PNG whenever the drawing changes.
 * Props:
 *  - value: initial data URL (pre-loads the pad on mount)
 *  - label: string shown above the canvas
 *  - onChange(dataUrl): called after each stroke ends
 *  - onClear(): optional — called when user clears
 *  - testid: root data-testid
 */
export default function SignaturePad({ value = "", label = "", onChange, onClear, testid = "signature-pad" }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(!!value);

  // Fit the canvas to its CSS width (handles DPR too so lines stay crisp).
  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B1220";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  // Load initial value (draw it centered).
  const loadValue = (dataUrl) => {
    if (!dataUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      setHasInk(true);
    };
    img.src = dataUrl;
  };

  useEffect(() => {
    resize();
    loadValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => {
      // Preserve current drawing across resize.
      const canvas = canvasRef.current;
      if (!canvas) return;
      const snapshot = (() => {
        try { return canvas.toDataURL("image/png"); } catch { return ""; }
      })();
      resize();
      loadValue(snapshot);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getPos = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const e = evt.touches ? evt.touches[0] : evt;
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const drawing = useRef(false);
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setHasInk(true);
    if (canvasRef.current && onChange) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    resize();
    setHasInk(false);
    if (onChange) onChange("");
    if (onClear) onClear();
  };

  return (
    <div data-testid={testid}>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <label className="canales-overline">{label}</label>
          {hasInk && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800"
              data-testid={`${testid}-clear`}
            >
              <Eraser size={12} /> {t("common.clear", "Clear")}
            </button>
          )}
        </div>
      )}
      <div
        className="relative rounded-md border border-stone-300 bg-white overflow-hidden"
        style={{ touchAction: "none" }}
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-32 cursor-crosshair select-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
        {!hasInk && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-stone-300 text-[11px] uppercase tracking-widest gap-1">
            <PenSquare size={12} /> {t("summary.sign_here", "Sign here")}
          </div>
        )}
      </div>
    </div>
  );
}
